import {
  Button,
  Center,
  Flex,
  HStack,
  IconButton,
  Image,
  Text,
  Tooltip,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  PopoverArrow,
  PopoverCloseButton,
  Box,
} from "@chakra-ui/react";
import { WarningTwoIcon } from "@chakra-ui/icons";
import { useTranslation } from "react-i18next";
import { AIDataType } from "../../types/Writer/AIData";
import { pxToRem } from "../../utils/editor.ts";
import useAiTypeStore from "../../stores/modules/aiType.ts";
import useOutlineStore from "../../stores/modules/outline.ts";
import React, { useEffect, useState } from "react";
import InsertTable from "./InserTool/InsertTable.tsx";
import { Editor } from "@tiptap/core";
import FindDialog from './FindDialog';
import AutoGenModal from './AutoGenModal.tsx';

enum ToolType {
  Bold = "strong",
  Italic = "em",
  Underline = "u",
  Equation = "equation",
  Figure = "figure",
  Table = "table",
  Foot = "footnote",
  Undo = "undo",
  Redo = "redo",
  Code = "code",
  Preview = "preview",
  Latex = "latex",
  FigureLatex = "figure_latex",
  Tablelatex = "Tablelatex",
  FigureTable = "figure_table",
  FigureTableLatex = "figure_table_latex",
  Find = "Find"
}
const ToolAIBox: React.FC<{
  editor: Editor;
  editorRef: any;
  setIsPaperViewerOpen: (open: boolean) => void;
  paperId?: string;
}> = ({ editor, editorRef, setIsPaperViewerOpen, paperId }) => {
  const { t } = useTranslation();
  const { aiType, updateAiType } = useAiTypeStore();
  const { outline, currentSectionIndex } = useOutlineStore();
  const [showReorganizeWarning, setShowReorganizeWarning] = useState(false);
  const basicTools = [
    // { img: '/static/ToolAI/b.png', label: t("toolbox-bold"), type: ToolType.Bold },
    { img: "/static/ToolAI/undo.png", label: "Undo", type: ToolType.Undo },
    { img: "/static/ToolAI/redo.png", label: "Redo", type: ToolType.Redo },
    {
      img: "/static/ToolAI/equation.png",
      label: t("toolbox-math"),
      type: ToolType.Equation,
    },
    {
      img: "/static/ToolAI/image.png",
      label: t("toolbox-image"),
      type: ToolType.Figure,
    },
    {
      img: "/static/ToolAI/table.png",
      label: t("toolbox-table"),
      type: ToolType.Table,
    },
    {
      img: "/static/ToolAI/footnote.png",
      label: t("toolbox-footnote"),
      type: ToolType.Foot,
    },
    { img: "/static/ToolAI/code.png", label: "Code", type: ToolType.Code },
    { img: "", label: "LaTex", type: ToolType.Latex },
    {
      img: "/latex/preview.png",     
      label: t("toolbox-preview"),
      type: ToolType.Preview,
    }, 
    {
      img: "/static/ToolAI/receipt-search.svg",
      label: 'Find',
      type: ToolType.Find,
    },
  ];
  const AIBtns = [
    {
      img: "/static/ToolAI/refine.svg",
      label: t("editor-editormodel-AI-Refine"),
      type: AIDataType.refine,
      tooltip: "Refine selected texts.",
    },
    {
      img: "/static/ToolAI/magicpen.png",
      label: t("editor-editormodel-AI-Rephrase"),
      type: AIDataType.rephrase,
      tooltip: "Paraphrase selected texts.",
    },
    {
      img: "/static/ToolAI/clipboard-tick.png",
      label: t("editor-editormodel-AI-Suggestions"),
      type: AIDataType.suggestion,
      tooltip: "Check for content coherence and completeness.",
    },
    {
      img: "/static/ToolAI/layer.png",
      label: t("editor-editormodel-AI-Reorganize"),
      type: AIDataType.reorganize,
      tooltip: "Explore how reorganization could improve your draft.",
    },
    {
      img: "/static/ToolAI/generation.svg",
      label: t("editor-editormodel-AI-Generation"),
      type: AIDataType.generation,
      tooltip: "Automatic generation of an abstract, a summary, or a conclusion based on the menuscript.",
    },

  ];
  const [openSelectTable, setOpenSelectTable] = useState(false);
  const [showFindDialog, setShowFindDialog] = useState(false);
  const [isAbstractModalOpen, setIsAbstractModalOpen] = useState(false);
  const [modalType, setModalType] = useState<'abstract' | 'summary' | 'conclusion'>('abstract');
  const [tablePoi] = useState<{
    x: number;
    y: number;
  }>({ x: 0, y: 0 });
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);

  useEffect(() => {
    if (!editor) return;

    const update = () => {
      setCanUndo((editor.can() as any).undo());
      setCanRedo((editor.can() as any).redo());
    };

    update();

    editor.on("update", update);

    return () => {
      editor.off("update", update);
    };
  }, [editor]);

  const onClickTool = (btn: ToolType, e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    // 检查当前节点是否为 level1
    const isLevel1 = editor?.isActive({ level: 1 });
    const isHeading = editor?.isActive("heading");
    // 对于块级元素，如果在标题中则阻止插入
    if (
      (isLevel1 || isHeading) &&
      [ToolType.Code, ToolType.Table, ToolType.Figure].includes(btn)
    ) {
      // 先将光标移动到标题的末尾
      editor.commands.selectTextblockEnd();
      // 然后创建新段落
      editor.commands.createParagraphNear();
      // 确保光标在新创建的段落中
      editor.commands.focus();
    }

    switch (btn) {
      // case ToolType.Bold:
      // case ToolType.Italic:
      // case ToolType.Underline: {
      //   applyFormattingToSelection(btn)
      //   node.content = domToContent(node)?.content;
      //   setNodeList([...newNodeList]);
      //   setOutlineIsChange(true)
      //   break
      // }
      case ToolType.Undo: {
        editorRef.current?.editor?.commands.undo();
        break;
      }
      case ToolType.Redo: {
        editorRef.current?.editor?.commands.redo();
        break;
      }
      case ToolType.Code: {
        editor
          .chain()
          .focus()
          .insertContent({
            type: "codeBlock",
            attrs: { language: "typescript" },
            content: [],
          })
          .run();
        break;
      }
      case ToolType.Figure: {
        editorRef.current?.openFigure({ type: "figure" });
        break;
      }

      case ToolType.FigureLatex: {
        editorRef.current?.openFigure({ type: "figure_latex" });
        break;
      }

      case ToolType.Equation: {
        editorRef.current?.insertLatex("inline");
        break;
      }
      // case ToolType.Table: {
      //   setTablePoi({
      //     x: e.clientX,
      //     y: e.clientY
      //   })
      //   setOpenSelectTable(true)
      //   break
      // }
      case ToolType.Foot: {
        editorRef.current?.openFoot(true);
        break;
      }
      case ToolType.Latex: {
        editorRef.current?.editor
          .chain()
          .focus()
          .splitBlock() // 分割当前块，创建新行
          .toggleCodeBlock({ language: "latex", useType: "LaTeX" } as any)
          .insertContent(`% Your LaTeX code here`)
          .run();
        break;
      }
      case ToolType.Preview: {
        setIsPaperViewerOpen(true);
        break;
      }

      case ToolType.Find: {
        setShowFindDialog(true);
        break;
      }

      case ToolType.Tablelatex: {
        editorRef.current?.editor
          ?.chain()
          .focus()
          .splitBlock() // 分割当前块，创建新行
          .toggleCodeBlock({ language: "latex", useType: "Table" } as any)
          .insertContent({
            type: "text",
            text: `\\begin{table}[h]
\\centering
\\begin{tabular}{|c|c|}
\\hline
Column 1 & Column 2 \\\\
\\hline
Data 1 & Data 2 \\\\
Data 3 & Data 4 \\\\
\\hline
\\end{tabular}
\\caption{Enter caption here}
\\label{Enter label here}
\\end{table}`,
          })
          .run();
        break;
      }

      case ToolType.FigureTable: {
        editorRef.current?.openFigure({ type: "table_figure" });
        break;
      }

      case ToolType.FigureTableLatex: {
        editorRef.current?.openFigure({ type: "table_figure_latex" });
        break;
      }
    }

    if ([ToolType.Code, ToolType.Latex, ToolType.Tablelatex].includes(btn)) {
      const { doc, selection } = editor.state;
      const isAtEnd = selection.anchor === doc.content.size - 1;
      if (isAtEnd) {
        editor.chain().insertContent({
          type: "paragraph",
        }).setTextSelection(selection.anchor + 2).run();
      }
    }
  };

  // const handleCreateTable = (e) => {
  //   setTablePoi({
  //     x: e.clientX,
  //     y: e.clientY,
  //   });
  //   setOpenSelectTable(true);
  // };

  const addIcon = (
    <Image
      sizes="xs"
      w={pxToRem(24)}
      objectFit="cover"
      src="/static/editor/add-table.svg"
      alt="no draft"
    />
  );

  const uploadIcon = (
    <Flex position={"relative"}>
      <Image
        sizes="xs"
        w={pxToRem(24)}
        objectFit="cover"
        src="/static/editor/upload-bg.svg"
        alt="no draft"
      />
      <Image
        sizes="xs"
        w={pxToRem(16)}
        position={"absolute"}
        left={pxToRem(4)}
        top={pxToRem(4)}
        objectFit="cover"
        src="/static/editor/upload.svg"
        alt="no draft"
      />
    </Flex>
  );

  return (
    <Center position={"absolute"} left={'1rem'} top={'-3.5rem'} zIndex={999}>
      <HStack
        bg={"#FFFFFF"}
        border={"1px solid #ccc"}
        // width={pxToRem(1050)}
        height={pxToRem(48)}
        borderRadius={"10px"}
        gap={0}
        // ml={pxToRem(80)}
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <Flex
          borderRight={"1px solid #D3D7E2"}
          width={pxToRem(420)}
          height={pxToRem(48)}
          pl={pxToRem(15)}
          pr={pxToRem(15)}
          gap={pxToRem(16)}
          justifyContent={"start"}
          alignItems={"center"}
        >
          {basicTools.map((tool) => {
            const isDisabled =
              (tool.type === ToolType.Undo && !canUndo) ||
              (tool.type === ToolType.Redo && !canRedo);
            return (
              <>
                {tool.type === ToolType.Table ? (
                  <Menu>
                    <Tooltip
                      hasArrow
                      placement={"bottom"}
                      borderRadius={pxToRem(6)}
                      key={tool.label}
                      padding={4}
                      bg={"#FFF"}
                      color={"#333"}
                      label={
                        <Text mb={0} fontSize={"1rem"} fontFamily={"Lato"}>
                          {tool.label}
                        </Text>
                      }
                      aria-label="import"
                    >
                      <MenuButton
                        as={IconButton}
                        size={"xs"}
                        bgColor={"transparent"}
                        _hover={{ bgcolor: "transparent" }}
                        data-no-focus={"true"}
                        icon={
                          <Image
                            w={pxToRem(26)}
                            h={pxToRem(26)}
                            objectFit="cover"
                            src={tool.img}
                            alt="Table"
                          />
                        }
                        aria-label={tool.label}
                      />
                    </Tooltip>
                    <MenuList zIndex={1000}>
                      {/* <MenuItem
                        icon={addIcon}
                        onClick={(e) => handleCreateTable(e)}
                        color="#333333"
                      >
                        Create
                      </MenuItem> */}
                      <MenuItem
                        icon={addIcon}
                        onClick={(e) => onClickTool(ToolType.Tablelatex, e)}
                        color="#333333"
                      >
                        Create with LaTeX
                      </MenuItem>
                      <MenuItem
                        icon={uploadIcon}
                        onClick={(e) => onClickTool(ToolType.FigureTable, e)}
                        color="#333333"
                      >
                        Upload Image
                      </MenuItem>
                      <MenuItem
                        icon={uploadIcon}
                        onClick={(e) =>
                          onClickTool(ToolType.FigureTableLatex, e)
                        }
                        color="#333333"
                      >
                        Upload Image with LaTeX
                      </MenuItem>
                    </MenuList>
                  </Menu>
                ) : (
                  <>
                    {tool.type === ToolType.Latex ? (
                      <Flex
                        border={"1px solid #333"}
                        cursor={"pointer"}
                        justifyContent={"center"}
                        w={pxToRem(56)}
                        alignItems={"center"}
                        borderRadius={pxToRem(4)}
                        fontSize={pxToRem(12)}
                        onClick={(e) => onClickTool(tool.type, e)}
                      >
                        LaTeX
                      </Flex>
                    ) : (
                      <>
                        {tool.type === ToolType.Figure ? (
                          <Menu>
                            <Tooltip
                              hasArrow
                              placement={"bottom"}
                              borderRadius={pxToRem(6)}
                              key={tool.label}
                              padding={4}
                              bg={"#FFF"}
                              color={"#333"}
                              label={
                                <Text
                                  mb={0}
                                  fontSize={"1rem"}
                                  fontFamily={"Lato"}
                                >
                                  {tool.label}
                                </Text>
                              }
                              aria-label="import"
                            >
                              <MenuButton
                                as={IconButton}
                                size={"xs"}
                                bgColor={"transparent"}
                                _hover={{ bgcolor: "transparent" }}
                                data-no-focus={"true"}
                                icon={
                                  <Image
                                    w={pxToRem(26)}
                                    h={pxToRem(26)}
                                    objectFit="cover"
                                    src={tool.img}
                                    alt="Table"
                                  />
                                }
                                aria-label={tool.label}
                              />
                            </Tooltip>
                            <MenuList zIndex={1000}>
                              <MenuItem
                                icon={uploadIcon}
                                onClick={(e) => onClickTool(ToolType.Figure, e)}
                                color="#333333"
                              >
                                Upload
                              </MenuItem>
                              <MenuItem
                                icon={uploadIcon}
                                onClick={(e) =>
                                  onClickTool(ToolType.FigureLatex, e)
                                }
                                color="#333333"
                              >
                                Upload with LaTeX
                              </MenuItem>
                            </MenuList>
                          </Menu>
                        ) : (
                          <Tooltip
                            hasArrow
                            placement={"bottom"}
                            borderRadius={pxToRem(6)}
                            key={tool.label}
                            padding={4}
                            bg={"#FFF"}
                            color={"#333"}
                            label={
                              <Text
                                mb={0}
                                fontSize={"1rem"}
                                fontFamily={"Lato"}
                              >
                                {tool.label}
                              </Text>
                            }
                            aria-label="import"
                          >
                            <IconButton
                              key={tool.type}
                              disabled={isDisabled}
                              // disabled={!currentCursor.nodeIndex === -1}
                              size={"xs"}
                              bgColor={"transparent"}
                              _hover={{ bgcolor: "transparent" }}
                              data-no-focus={"true"}
                              icon={
                                <Image
                                  w={pxToRem(26)}
                                  h={pxToRem(26)}
                                  transform={
                                    tool.type === ToolType.Foot ||
                                    tool.type === ToolType.Find ||
                                    tool.type === ToolType.Preview
                                      ? `scale(0.65)`
                                      : "scale(1)"
                                  }
                                  objectFit="cover"
                                  src={tool.img}
                                  alt="Doenba"
                                />
                              }
                              aria-label={tool.label}
                              onClick={(e) => onClickTool(tool.type, e)}
                            />
                          </Tooltip>
                        )}
                      </>
                    )}
                  </>
                )}
              </>
            );
          })}
        </Flex>
        <Flex
          // width={pxToRem(569)}
          flex={1}
          alignItems={"center"}
          justifyContent={"start"}
          // pl={pxToRem(20)}
          pr={pxToRem(20)}
          gap={pxToRem(6)}
        >
          {AIBtns.map((btn) => {
            if (btn.type === AIDataType.generation) {
              return (
                <Menu key={btn.label}>
                  <Tooltip
                    hasArrow
                    placement={"bottom"}
                    borderRadius={pxToRem(6)}
                    padding={4}
                    bg={"#FFF"}
                    color={"#333"}
                    label={
                      <Text mb={0} fontSize={"1rem"} fontFamily={"Lato"}>
                        {btn.tooltip}
                      </Text>
                    }
                    aria-label="import"
                  >
                    <MenuButton
                      as={Button}
                      pl={'0.5rem'}
                      height={pxToRem(36)}
                      w={pxToRem(160)}
                      value={btn.label}
                      bgColor={btn.type === aiType ? "#C9D9E8" : "transparent"}
                      fontWeight="bold"
                      textColor={"#333333"}
                      leftIcon={
                        <Image
                          w={pxToRem(24)}
                          objectFit="cover"
                          src={btn.img}
                          alt="Generation"
                        />
                      }
                    >
                      {btn.label}
                    </MenuButton>
                  </Tooltip>
                  <MenuList zIndex={1000}>
                    <MenuItem
                      icon={
                        <Image
                          w={pxToRem(20)}
                          h={pxToRem(20)}
                          objectFit="cover"
                          src="/static/ToolAI/summary.svg"
                          alt="Summary"
                        />
                      }
                      onClick={() => {
                        setModalType('summary');
                        setIsAbstractModalOpen(true);
                      }}
                      color="#333333"
                    >
                      {t("editor-editormodel-AI-Summary")}
                    </MenuItem>
                    <MenuItem
                      icon={
                        <Image
                          w={pxToRem(20)}
                          h={pxToRem(20)}
                          objectFit="cover"
                          src="/static/ToolAI/abstract.svg"
                          alt="Abstract"
                        />
                      }
                      onClick={() => {
                        setModalType('abstract');
                        setIsAbstractModalOpen(true);
                      }}
                      color="#333333"
                    >
                      {t("editor-editormodel-AI-Abstract")}
                    </MenuItem>
                    <MenuItem
                      icon={
                        <Image
                          w={pxToRem(20)}
                          h={pxToRem(20)}
                          objectFit="cover"
                          src="/static/ToolAI/conclusion.svg"
                          alt="Conclusion"
                        />
                      }
                      onClick={() => {
                        setModalType('conclusion');
                        setIsAbstractModalOpen(true);
                      }}
                      color="#333333"
                    >
                      {t("editor-editormodel-AI-Conclusion")}
                    </MenuItem>
                  </MenuList>
                </Menu>
              );
            }

            const buttonContent = (
              <Tooltip
                hasArrow
                placement={"bottom"}
                borderRadius={pxToRem(6)}
                key={btn.label}
                padding={4}
                bg={"#FFF"}
                color={"#333"}
                label={
                  <Text mb={0} fontSize={"1rem"} fontFamily={"Lato"}>
                    {btn.tooltip}
                  </Text>
                }
                aria-label="import"
              >
                <Button
                  height={pxToRem(36)}
                  value={btn.label}
                  bgColor={btn.type === aiType ? "#C9D9E8" : "transparent"}
                  fontWeight="bold"
                  textColor={"#333333"}
                  onClick={() => {
                    if (btn.type === AIDataType.reorganize) {
                      const currentSection = outline?.nodes?.[currentSectionIndex];
                      const allowedNames = ["Abstract", "Summary", "Introduction", "Conclusion"];
                      if (currentSection && currentSection.name && allowedNames.includes(currentSection.name)) {
                        if (btn.type === aiType) {
                          updateAiType(null);
                        } else {
                          updateAiType(btn.type);
                        }
                      } else {
                        setShowReorganizeWarning(true);
                      }
                    } else {
                      if (btn.type === aiType) {
                        updateAiType(null);
                      } else {
                        updateAiType(btn.type);
                      }
                    }
                  }}
                  leftIcon={
                    <Image
                      w={pxToRem(24)}
                      objectFit="cover"
                      src={btn.img}
                      alt="Doenba"
                    />
                  }
                >
                  {btn.label}
                </Button>
              </Tooltip>
            );

            if (btn.type === AIDataType.reorganize) {
              return (
                <Popover
                  isOpen={showReorganizeWarning}
                  onClose={() => setShowReorganizeWarning(false)}
                  placement="bottom"
                  key={btn.label}
                >
                  <PopoverTrigger>
                    <Box display="inline-block">{buttonContent}</Box>
                  </PopoverTrigger>
                  <PopoverContent bg='orange.50' borderColor='orange.200' borderWidth='1px' color='orange.900' w='auto' maxW={pxToRem(325)}>
                    <PopoverArrow bg='orange.50' />
                    <PopoverCloseButton size="sm" color='orange.900' />
                    <PopoverBody fontSize={pxToRem(14)} p={3}>
                      <Flex align="start" gap={2}>
                        <WarningTwoIcon color="orange.500" boxSize={5} mt="2px" />
                        <Text as="span" fontWeight="medium">
                          The Reorganize function applies only to the Abstract, Conclusion, Introduction, and Summary.
                        </Text>
                      </Flex>
                    </PopoverBody>
                  </PopoverContent>
                </Popover>
              );
            }

            return buttonContent;
          })}
        </Flex>
        <InsertTable
          isOpen={openSelectTable}
          editor={editor}
          setIsOpen={setOpenSelectTable}
          tablePoi={tablePoi}
        />
      </HStack>

      <FindDialog
        isOpen={showFindDialog}
        onClose={() => setShowFindDialog(false)}
        editor={editor}
      />

      <AutoGenModal
        isOpen={isAbstractModalOpen}
        onClose={() => setIsAbstractModalOpen(false)}
        paperId={paperId}
        type={modalType}
      />
    </Center>
  );
};
export default ToolAIBox;
